import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type {
    CircuitBreakerCallResponse,
    CircuitBreakerStateResponse,
    CircuitBreakerResetResponse,
    CircuitBreakerAllStatesResponse,
} from '../types';

const QUERY_KEY = 'circuit-breaker-state';

export interface CallCircuitBreakerParams {
    simulateFailure?: boolean;
    delayMs?: number;
}

export function useCircuitBreakerState() {
    return useQuery({
        queryKey: [QUERY_KEY],
        queryFn: () => api.get<CircuitBreakerStateResponse>('/api/circuit-breaker-test/state'),
        refetchInterval: 5000,
    });
}

export function useCircuitBreakerAllStates() {
    return useQuery({
        queryKey: [QUERY_KEY, 'all'],
        queryFn: () => api.get<CircuitBreakerAllStatesResponse>('/api/circuit-breaker-test/states'),
        refetchInterval: 5000,
    });
}

export function useCallCircuitBreaker() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CallCircuitBreakerParams = {}) => {
            const searchParams = new URLSearchParams();
            if (params.simulateFailure) {
                searchParams.set('simulate_failure', 'true');
            }
            if (params.delayMs) {
                searchParams.set('delay_ms', params.delayMs.toString());
            }

            const queryString = searchParams.toString();
            const url = `/api/circuit-breaker-test/call${queryString ? `?${queryString}` : ''}`;

            // Use apiFetch directly to handle non-2xx responses
            const response = await fetch(
                `${import.meta.env.VITE_API_URL || ''}${url}`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                },
            );

            return response.json() as Promise<CircuitBreakerCallResponse>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });
}

export function useResetCircuitBreaker() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            api.post<CircuitBreakerResetResponse>('/api/circuit-breaker-test/reset'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });
}
